import { gql, useLazyQuery } from '@apollo/client';
import classNames from 'classnames';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import merge from 'lodash/merge';

import { CustomTooltipInfo, Heading, Surface } from '@quality24/design-system';
import { useOnScreen } from '@quality24/react-hooks';

import {
  BadgeComponentData,
  BarChartComponentData,
  CardComponentData,
  Customizations,
  DataViewData,
  DonutChartComponentData,
  ExportButtonHandoff,
  GaugeChartComponentData,
  GraphQLFilter,
  HasPermissionFn,
  LineChartComponentData,
  PieChartComponentData,
  TabbedFilterComponentData,
  TableComponentData,
} from '../../@types';

import { useQueryFilter } from '../../hooks/filterHooks';

import {
  formatSearchFilterId,
  mergePermissionFilter,
} from '../../utils/filterUtils';
import { getDateTruncate } from '../../utils/momentUtils';

import FilterViewContainer from '../FilterViewContainer';
import ExportDataButtonContainer from './components/ExportDataButtonContainer';
import BadgeContainer from './components/BadgeContainer';
import BarChartContainer from './components/BarChartContainer';
import LineChartContainer from './components/LineChartContainer';
import PieChartContainer from './components/PieChartContainer';
import DonutChartContainer from './components/DonutChartContainer';
import GaugeChartContainer from './components/GaugeChartContainer';
import TableContainer from './components/TableContainer';

import CardContainer from './components/CardContainer';

import styles from './DataViewContainer.module.scss';
import { getSubscriptionHandler } from './utils/subscription';

// Stable default for `baseFilter`/`permissionFilter` — an inline `= {}` default
// parameter would otherwise construct a brand-new object every render when the
// caller omits the prop, defeating memoization of anything derived from it.
const EMPTY_FILTER: GraphQLFilter = {};

export interface Props extends React.HTMLAttributes<HTMLElement> {
  data: DataViewData;
  customizations?: Customizations;
  baseFilter?: GraphQLFilter;
  permissionFilter?: GraphQLFilter;
  hasPermission?: HasPermissionFn;
  /**
   * When provided, the resolved export-button config is reported here instead
   * of being rendered inline — used by a parent container (e.g.
   * `SwitchTabViewContainer`) to render the button in its own header.
   */
  onExportReady?: (handoff: ExportButtonHandoff | null) => void;
}

/**
 * <DataViewContainer> page
 */
const DataViewContainer: React.FunctionComponent<Props> = ({
  className,
  data: {
    title,
    outerClassName,
    className: configClassName,
    tooltipContent,
    generalFilters,
    specificFilters,
    filterModifiers,
    permissionFilters,
    options,
    customQueryFilter,
    customQueryVariables,
    query,
    subscription,
    components,
    exportDefinition,
  },
  customizations,
  baseFilter = EMPTY_FILTER,
  permissionFilter: userPermissionFilter = EMPTY_FILTER,
  hasPermission,
  onExportReady,
  ...htmlElementProps
}) => {
  // Create a component id
  const idRef = React.useRef(uuid());

  // Check if this component is visible on screen
  const elementRef = React.useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(elementRef);

  // Fetch filters. The keys array is memoized so `getGraphQLFilter` (from
  // `useQueryFilter`, itself memoized off this array) stays referentially
  // stable across renders that don't actually change the filter selection —
  // otherwise anything depending on `getGraphQLFilter`'s identity (like the
  // export-handoff memo below) would recompute on every render, which for a
  // reported value feeding a parent's state setter causes an infinite loop.
  const filterKeys = React.useMemo(
    () => [
      ...(generalFilters || []),

      // Find specific filters id (these should be parsed due to special search filter)
      ...(specificFilters?.flatMap((f) => {
        if (f.type === 'search') return formatSearchFilterId(f.id);
        if (f.type === 'tabbed') {
          return (f as TabbedFilterComponentData).components.map((c) => c.id);
        }
        return f.id;
      }) || []),
    ],
    [generalFilters, specificFilters],
  );

  const { getGraphQLFilter, getDateFilter, getPermissionFilter } =
    useQueryFilter(filterKeys, idRef.current);

  // Table pagination states
  const [limit, setLimit] = React.useState(10);
  const [offset, setOffset] = React.useState(0);

  // Retrieve and memoize the graphQLFilters
  // This getter applies any usage modifications
  const graphQLFilter = React.useMemo(
    () => getGraphQLFilter(filterModifiers),
    [getGraphQLFilter, filterModifiers],
  );

  // Date filter
  const dateFilter = React.useMemo(() => getDateFilter(), [getDateFilter]);

  // Whether components has table def
  const hasTable = React.useMemo(
    () => components.some((c) => c.type === 'table' && c.options?.pagination),
    [components],
  );

  // Account restrictions this view opted into. Resolved here (instead of inside
  // the filter memo) because the export query has to be restricted too.
  const permissionFilter = React.useMemo(
    () => getPermissionFilter(userPermissionFilter, permissionFilters),
    [getPermissionFilter, userPermissionFilter, permissionFilters],
  );

  const filter = React.useMemo(() => {
    if (
      (baseFilter && Object.keys(baseFilter).length > 0) ||
      (graphQLFilter && Object.keys(graphQLFilter).length > 0) ||
      (permissionFilters && permissionFilters.length > 0) ||
      (customQueryFilter && Object.keys(customQueryFilter).length > 0)
    ) {
      const parsedFilters = mergePermissionFilter(
        merge({}, baseFilter, customQueryFilter, graphQLFilter),
        permissionFilter,
      );

      return Object.keys(parsedFilters).length ? parsedFilters : undefined;
    }
    return undefined;
  }, [
    baseFilter,
    customQueryFilter,
    graphQLFilter,
    permissionFilter,
    permissionFilters,
  ]);

  // Check if query defines any dash-variable (_varName) and set default value to null
  // @note this prevents graphql from breaking due to empty filter
  const dashVariables = React.useMemo(() => {
    const queryDef = gql([query]).definitions[0];
    if (queryDef.kind !== 'OperationDefinition') return filter;

    const vars =
      queryDef.variableDefinitions?.reduce<
        Record<string, GraphQLFilter | null>
      >((acc, def) => {
        if (def.variable.name.value.startsWith('_')) {
          acc[def.variable.name.value.replace('_', '')] = null;
        }
        return acc;
      }, {}) || {};
    return {
      ...vars,
      ...filter,
    };
  }, [filter, query]);

  // Prepare the query to eventually fetch filter
  const [
    fetch,
    { data, loading, error, fetchMore, subscribeToMore, refetch, called },
  ] = useLazyQuery(gql([query]), {
    variables: {
      ...customQueryVariables,
      filter,
      limit: hasTable ? limit : undefined,
      offset: hasTable ? offset : undefined,
      dateTrunc: dateFilter ? getDateTruncate(dateFilter) : undefined,
      clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...Object.fromEntries(
        Object.entries(dashVariables || {}).map(([key, value]) => [
          `_${key}`,
          value,
        ]),
      ),
    },
  });

  React.useEffect(() => {
    if (!query || !isVisible || called) return;
    fetch({
      initialFetchPolicy: 'network-only',
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'cache-only',
    });
  }, [query, fetch, isVisible, called]);

  /**
   * Subscribe to changes
   */
  React.useEffect(() => {
    if (!subscription || !subscribeToMore) return () => null;

    const unsubscribe = subscribeToMore({
      document: gql([subscription.definition]),
      updateQuery: getSubscriptionHandler(
        subscription.rootKey,
        subscription.eventKey,
        subscription.queryRootKey,
        subscription.postQuery,
        subscription.refetch ? refetch : undefined,
      ),
    });

    return () => unsubscribe();
  }, [refetch, subscribeToMore, subscription]);

  // Memoize the filters to render
  const bellowFilters = React.useMemo(
    () => specificFilters?.filter((f) => f.position === 'bellow'),
    [specificFilters],
  );
  const headerFilters = React.useMemo(
    () =>
      specificFilters?.filter((f) => !f.position || f.position === 'header'),
    [specificFilters],
  );

  // Resolved export-button props — reused both for the inline render (when no
  // `onExportReady` is provided) and to report upward to a parent container.
  const exportHandoff = React.useMemo<ExportButtonHandoff | null>(() => {
    if (!exportDefinition) return null;
    return {
      config: exportDefinition,
      filters: mergePermissionFilter(
        {
          ...customQueryFilter,
          ...exportDefinition.customQueryFilter,
          ...getGraphQLFilter(
            exportDefinition.filterModifiers ?? filterModifiers,
          ),
        },
        permissionFilter,
      ),
      customQueryVariables: {
        ...customQueryVariables,
        ...exportDefinition.customQueryVariables,
      },
      data,
      disabled: loading || !!error,
    };
  }, [
    exportDefinition,
    customQueryFilter,
    getGraphQLFilter,
    filterModifiers,
    permissionFilter,
    customQueryVariables,
    data,
    loading,
    error,
  ]);

  // Report the export button config to a parent container instead of
  // rendering it inline. `useLayoutEffect` avoids a visible flash of a missing
  // button whenever a switch-tab-view pane mounts/remounts on tab switch.
  React.useLayoutEffect(() => {
    if (!onExportReady) return undefined;
    onExportReady(exportHandoff);
    return () => onExportReady(null);
  }, [onExportReady, exportHandoff]);

  const showsInlineExport = !!exportDefinition && !onExportReady;

  return (
    <div
      ref={elementRef}
      className={classNames(className, outerClassName)}
      {...htmlElementProps}
    >
      <Surface
        className={classNames(styles.surface, {
          'on-surface': options?.renderOnSurface !== false,
        })}
        padding="sm"
        backgroundColor="grayLight1"
      >
        <div
          className={classNames(styles.header, {
            'mb-2': title || showsInlineExport || headerFilters,
            'mb-3': title && !showsInlineExport && !headerFilters,
          })}
        >
          <div className="d-flex flex-items-center gap-2">
            {title && (
              <Heading as="h2" size="sm2" weight="bold" className="flex-grow-1">
                {title}
              </Heading>
            )}
            {tooltipContent && <CustomTooltipInfo info={tooltipContent} />}
          </div>

          <div className={styles['filter-wrapper']}>
            {/* Additional filters */}
            {headerFilters && (
              <FilterViewContainer
                className="d-flex flex-justify-end"
                partitionId={idRef.current}
                data={{ components: headerFilters }}
                hasPermission={hasPermission}
              />
            )}

            {/* Export button */}
            {showsInlineExport && exportHandoff && (
              <ExportDataButtonContainer
                config={exportHandoff.config}
                filters={exportHandoff.filters}
                customQueryVariables={exportHandoff.customQueryVariables}
                data={data}
                disabled={exportHandoff.disabled}
                hasPermission={hasPermission}
              />
            )}
          </div>
        </div>

        <Surface
          className={classNames(styles.surface, {
            'on-surface': options?.renderOnInnerSurface,
          })}
          padding="sm"
          backgroundColor="grayLight2"
        >
          {bellowFilters && (
            <div className="mb-3">
              <FilterViewContainer
                partitionId={idRef.current}
                data={{ components: bellowFilters }}
                hasPermission={hasPermission}
              />
            </div>
          )}

          <div className={configClassName}>
            {components.map(({ type, permission, ...config }, idx) => {
              // Here the array order does not change so we
              // can safely disable the eslint-warning
              const id = `${type}-${config.rootKey}-${idx}`;

              if (hasPermission && permission && !hasPermission(permission)) {
                return null;
              }

              if (type === 'badge') {
                return (
                  <BadgeContainer
                    key={id}
                    data={data}
                    loading={loading}
                    error={error}
                    refetch={refetch}
                    config={config as Omit<BadgeComponentData, 'type'>}
                  />
                );
              }
              if (type === 'card') {
                return (
                  <CardContainer
                    key={id}
                    data={data}
                    dateFilter={dateFilter}
                    loading={loading}
                    error={error}
                    refetch={refetch}
                    config={config as Omit<CardComponentData, 'type'>}
                  />
                );
              }
              if (type === 'pie-chart') {
                return (
                  <PieChartContainer
                    key={id}
                    data={data}
                    dateFilter={dateFilter}
                    loading={loading}
                    error={error}
                    refetch={refetch}
                    renderOnSurface={!options?.renderOnInnerSurface}
                    config={config as Omit<PieChartComponentData, 'type'>}
                  />
                );
              }
              if (type === 'donut-chart') {
                return (
                  <DonutChartContainer
                    key={id}
                    data={data}
                    dateFilter={dateFilter}
                    loading={loading}
                    error={error}
                    refetch={refetch}
                    renderOnSurface={!options?.renderOnInnerSurface}
                    config={config as Omit<DonutChartComponentData, 'type'>}
                  />
                );
              }
              if (type === 'bar-chart') {
                return (
                  <BarChartContainer
                    key={id}
                    data={data}
                    dateFilter={dateFilter}
                    loading={loading}
                    error={error}
                    refetch={refetch}
                    renderOnSurface={!options?.renderOnInnerSurface}
                    config={config as Omit<BarChartComponentData, 'type'>}
                  />
                );
              }
              if (type === 'line-chart') {
                return (
                  <LineChartContainer
                    key={id}
                    data={data}
                    dateFilter={dateFilter}
                    loading={loading}
                    error={error}
                    refetch={refetch}
                    renderOnSurface={!options?.renderOnInnerSurface}
                    config={config as Omit<LineChartComponentData, 'type'>}
                  />
                );
              }
              if (type === 'gauge-chart') {
                return (
                  <GaugeChartContainer
                    key={id}
                    data={data}
                    dateFilter={dateFilter}
                    loading={loading}
                    error={error}
                    refetch={refetch}
                    renderOnSurface={!options?.renderOnInnerSurface}
                    config={config as Omit<GaugeChartComponentData, 'type'>}
                  />
                );
              }
              if (type === 'table') {
                return (
                  <TableContainer
                    key={id}
                    data={data}
                    dateFilter={dateFilter}
                    loading={loading}
                    error={error}
                    refetch={refetch}
                    config={config as Omit<TableComponentData, 'type'>}
                    customizations={customizations}
                    // pagination
                    fetchMore={fetchMore}
                    pageSize={limit}
                    onChangePageSize={setLimit}
                    onChangePage={setOffset}
                    hasPermission={hasPermission}
                  />
                );
              }
              return null;
            })}
          </div>
        </Surface>
      </Surface>
    </div>
  );
};

export default DataViewContainer;
